/**
 * generateReceiptReport
 *
 * Builds a print-ready HTML document for the selected camp's registrations,
 * opens it in a new window, and triggers the browser's print dialog so the
 * admin can save it as a PDF.
 *
 * @param {object} camp            — The camp row from Supabase
 * @param {Array}  registrations   — Non-cancelled registration rows
 *                                   (each row should include pricing_tiers data)
 * @param {Array}  paymentReceipts — All payment_receipts rows for this camp
 *                                   (used for payment method info and receipt images)
 */
import i18n from '../i18n'
import { paymentMethodLabel } from './paymentMethods'

export function generateReceiptReport(camp, registrations, paymentReceipts = [], refundedRegistrations = []) {
  /* ── Helpers ─────────────────────────────────────────────────────────── */

  // Escape DB-sourced values before interpolating into the print HTML
  function esc(s) {
    return String(s ?? '')
      .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;').replaceAll("'", '&#39;')
  }

  function fmt(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    })
  }

  function fmtShort(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  }

  function currency(n) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0)
  }

  /* ── Derived values ───────────────────────────────────────────────────── */

  const generatedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })

  const campDateRange =
    camp.start_date && camp.end_date
      ? `${fmt(camp.start_date)} – ${fmt(camp.end_date)}`
      : camp.start_date
        ? fmt(camp.start_date)
        : ''

  const paid      = registrations.filter(r => r.payment_status === 'Paid')
  const partial   = registrations.filter(r => r.payment_status === 'Partial Payment')
  const totalCollected = paid.reduce((s, r) => s + (r.amount_paid ?? 0), 0)
  const totalPartial   = partial.reduce((s, r) => s + (r.amount_paid ?? 0), 0)

  // Build per-registration payment method summary from approved receipts
  const approvedReceipts = paymentReceipts.filter(r => r.status === 'Approved')
  const methodMap = {}
  approvedReceipts.forEach(r => {
    if (!methodMap[r.registration_id]) methodMap[r.registration_id] = []
    if (r.payment_method && !methodMap[r.registration_id].includes(r.payment_method)) {
      methodMap[r.registration_id].push(r.payment_method)
    }
  })

  /* ── Status badge ─────────────────────────────────────────────────────── */

  function badge(status) {
    const map = {
      Paid:             'background:#dcfce7;color:#166534;border:1px solid #bbf7d0',
      'Partial Payment':'background:#dbeafe;color:#1e40af;border:1px solid #bfdbfe',
      Confirmed:        'background:#f7fee7;color:#4d7c0f;border:1px solid #d9f99d',
      Pending:          'background:#fffbeb;color:#92400e;border:1px solid #fde68a',
    }
    const style = map[status] ?? 'background:#f3f4f6;color:#374151;border:1px solid #e5e7eb'
    return `<span style="${style};border-radius:999px;padding:2px 10px;font-size:11px;font-weight:600;display:inline-block;white-space:nowrap;">${status}</span>`
  }

  /* ── Table rows ───────────────────────────────────────────────────────── */

  const tableRows = registrations.map((r, i) => {
    const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb'
    const arrivalDate = fmtShort(r.pricing_tiers?.arrival_date ?? r.arrival_date)
    const methods = methodMap[r.id]?.map(m => paymentMethodLabel(m, i18n.t.bind(i18n)) ?? m).join(', ') || '—'
    return `
      <tr style="background:${bg};">
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;font-weight:500;color:#111827;">${esc(r.full_name)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#374151;white-space:nowrap;">${arrivalDate}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#374151;font-weight:600;">${currency(r.amount_owed)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#374151;font-weight:600;">${currency(r.amount_paid)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#374151;">${esc(methods)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;">${badge(r.payment_status)}</td>
      </tr>`
  }).join('')

  /* ── Receipt image blocks (from payment_receipts, not registrations) ──── */

  // Render one receipt card. badgeHtml is injected so the same markup serves
  // both the active section (status badge) and the refunded section (Refunded badge).
  function receiptBlock(r, reg, badgeHtml) {
    const isPdf = /\.pdf(\?|$)/i.test(r.receipt_url)
    const arrivalDate = fmtShort(reg.pricing_tiers?.arrival_date ?? reg.arrival_date)
    return `
      <div style="page-break-inside:avoid;margin-bottom:28px;padding:20px 24px;border:1px solid #e5e7eb;border-radius:12px;">
        <div style="display:flex;align-items:start;justify-content:space-between;flex-wrap:wrap;gap:8px;margin-bottom:14px;">
          <div>
            <p style="margin:0;font-size:14px;font-weight:700;color:#111827;">${esc(reg.full_name)}</p>
            <p style="margin:3px 0 0;font-size:12px;color:#6b7280;">
              ${arrivalDate} &nbsp;·&nbsp; ${esc(paymentMethodLabel(r.payment_method, i18n.t.bind(i18n)) ?? '—')} &nbsp;·&nbsp; ${currency(r.amount)}
            </p>
          </div>
          <div>${badgeHtml}</div>
        </div>
        ${isPdf
          ? `<a href="${esc(r.receipt_url)}" target="_blank" style="font-size:12px;color:#64795c;text-decoration:none;display:inline-flex;align-items:center;gap:6px;padding:10px 16px;border:1px solid #c0cab6;border-radius:8px;background:#eff1eb;">
               📄 <span>${i18n.t('admin.reportPdfClickToOpen')}</span>
             </a>`
          : `<img src="${esc(r.receipt_url)}" alt="${i18n.t('admin.reportRegistrationsSection')} — ${esc(reg.full_name)}"
               style="max-width:100%;max-height:520px;object-fit:contain;border-radius:8px;border:1px solid #f3f4f6;display:block;" />`
        }
      </div>`
  }

  const regMap = {}
  registrations.forEach(r => { regMap[r.id] = r })

  const receiptBlocks = approvedReceipts
    .filter(r => r.receipt_url)
    .map(r => {
      const reg = regMap[r.registration_id]
      if (!reg) return ''
      return receiptBlock(r, reg, badge(reg.payment_status))
    }).join('')

  /* ── Refunded attendees (paid then refunded) ──────────────────────────── */

  const refundedBadge = `<span style="background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;border-radius:999px;padding:2px 10px;font-size:11px;font-weight:600;display:inline-block;white-space:nowrap;">${i18n.t('admin.reportRefundedBadge')}</span>`

  const refundedRegMap = {}
  refundedRegistrations.forEach(r => { refundedRegMap[r.id] = r })

  // Income proof = sum of that registration's approved receipt amounts.
  const refundedIncomeByReg = {}
  approvedReceipts.forEach(r => {
    if (refundedRegMap[r.registration_id]) {
      refundedIncomeByReg[r.registration_id] =
        (refundedIncomeByReg[r.registration_id] ?? 0) + (r.amount ?? 0)
    }
  })

  const refundedIncomeTotal = refundedRegistrations.reduce(
    (s, reg) => s + (refundedIncomeByReg[reg.id] ?? 0), 0,
  )
  const refundedTotal = refundedRegistrations.reduce(
    (s, reg) => s + (reg.refund_amount ?? 0), 0,
  )

  const refundedTableRows = refundedRegistrations.map((reg, i) => {
    const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb'
    const arrivalDate = fmtShort(reg.pricing_tiers?.arrival_date ?? reg.arrival_date)
    const methods = methodMap[reg.id]?.map(m => paymentMethodLabel(m, i18n.t.bind(i18n)) ?? m).join(', ') || '—'
    return `
      <tr style="background:${bg};">
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;font-weight:500;color:#111827;">${esc(reg.full_name)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#374151;white-space:nowrap;">${arrivalDate}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#374151;font-weight:600;">${currency(refundedIncomeByReg[reg.id] ?? 0)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#b91c1c;font-weight:600;">${currency(reg.refund_amount)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;color:#374151;">${esc(methods)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6;">${refundedBadge}</td>
      </tr>`
  }).join('')

  const refundedReceiptList = approvedReceipts.filter(r => r.receipt_url && refundedRegMap[r.registration_id])
  const refundedReceiptBlocks = refundedReceiptList
    .map(r => receiptBlock(r, refundedRegMap[r.registration_id], refundedBadge))
    .join('')

  /* ── Full HTML document ───────────────────────────────────────────────── */

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Receipt Report — ${esc(camp.name)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      color: #111827;
      background: #ffffff;
      padding: 32px 40px;
      font-size: 14px;
      line-height: 1.5;
    }
    table { width: 100%; border-collapse: collapse; }
    th {
      background: #eff1eb;
      color: #3d4f3b;
      text-align: left;
      padding: 10px 14px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border-bottom: 2px solid #c0cab6;
      white-space: nowrap;
    }
    @page { margin: 20mm 16mm; size: A4; }
    @media print {
      .no-print { display: none !important; }
      body { padding: 0; font-size: 12px; }
    }
  </style>
</head>
<body>

  <!-- Print button (screen only) -->
  <div class="no-print" style="margin-bottom:28px;display:flex;align-items:center;gap:14px;padding:16px 20px;background:#eff1eb;border:1px solid #c0cab6;border-radius:12px;">
    <button
      onclick="window.print()"
      style="background:#64795c;color:#fff;border:none;padding:10px 22px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:8px;"
    >
      ${i18n.t('admin.reportSaveAsPdf')}
    </button>
    <span style="font-size:13px;color:#6b7280;">${i18n.t('admin.reportSaveAsPdfHint')}</span>
  </div>

  <!-- Report header -->
  <div style="border-bottom:3px solid #3d4f3b;padding-bottom:20px;margin-bottom:28px;">
    <div style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px;">
      <div>
        <p style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#64795c;margin-bottom:4px;">Oasis Joven Camp</p>
        <h1 style="font-size:22px;font-weight:800;color:#111827;margin-bottom:4px;">${i18n.t('admin.reportReceiptTitle')}</h1>
        <p style="font-size:15px;font-weight:600;color:#374151;margin-bottom:2px;">${esc(camp.name)}</p>
        ${campDateRange ? `<p style="font-size:13px;color:#9ca3af;">${campDateRange}${camp.location ? ' &nbsp;·&nbsp; ' + esc(camp.location) : ''}</p>` : ''}
      </div>
      <div style="text-align:right;">
        <p style="font-size:11px;color:#9ca3af;margin-bottom:2px;">${i18n.t('admin.reportGenerated')}</p>
        <p style="font-size:13px;font-weight:600;color:#374151;">${generatedDate}</p>
        <p style="font-size:12px;color:#9ca3af;margin-top:8px;">${registrations.length} registration${registrations.length !== 1 ? 's' : ''}</p>
      </div>
    </div>
  </div>

  <!-- Summary cards -->
  <div style="display:flex;gap:14px;margin-bottom:32px;flex-wrap:wrap;">
    <div style="flex:1;min-width:150px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:16px 20px;">
      <p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#16a34a;margin-bottom:4px;">${i18n.t('admin.reportTotalCollectedPaid')}</p>
      <p style="font-size:26px;font-weight:800;color:#15803d;">${currency(totalCollected)}</p>
      <p style="font-size:11px;color:#16a34a;margin-top:2px;">${paid.length} registration${paid.length !== 1 ? 's' : ''}</p>
    </div>
    <div style="flex:1;min-width:150px;background:#dbeafe;border:1px solid #bfdbfe;border-radius:12px;padding:16px 20px;">
      <p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#1d4ed8;margin-bottom:4px;">${i18n.t('admin.reportPartialPayments')}</p>
      <p style="font-size:26px;font-weight:800;color:#1e40af;">${currency(totalPartial)}</p>
      <p style="font-size:11px;color:#1d4ed8;margin-top:2px;">${partial.length} registration${partial.length !== 1 ? 's' : ''}</p>
    </div>
    <div style="flex:1;min-width:150px;background:#eff1eb;border:1px solid #c0cab6;border-radius:12px;padding:16px 20px;">
      <p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#64795c;margin-bottom:4px;">${i18n.t('admin.reportGrandTotalReceived')}</p>
      <p style="font-size:26px;font-weight:800;color:#64795c;">${currency(totalCollected + totalPartial)}</p>
      <p style="font-size:11px;color:#64795c;margin-top:2px;">${registrations.length} registration${registrations.length !== 1 ? 's' : ''}</p>
    </div>
  </div>

  <!-- Registrations table -->
  <h2 style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#374151;margin-bottom:12px;">${i18n.t('admin.reportRegistrationsSection')}</h2>
  <table style="margin-bottom:40px;">
    <thead>
      <tr>
        <th>${i18n.t('admin.reportColFullName')}</th>
        <th>${i18n.t('admin.reportColArrivalDate')}</th>
        <th>${i18n.t('admin.reportColAmountOwed')}</th>
        <th>${i18n.t('admin.reportColAmountPaid')}</th>
        <th>${i18n.t('admin.reportColPaymentMethods')}</th>
        <th>${i18n.t('admin.reportColStatus')}</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows || `<tr><td colspan="6" style="padding:24px;text-align:center;color:#9ca3af;">${i18n.t('admin.reportNoRegistrations')}</td></tr>`}
    </tbody>
  </table>

  ${receiptBlocks ? `
  <!-- Receipt images section -->
  <h2 style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#374151;margin-bottom:16px;border-top:2px solid #f3f4f6;padding-top:28px;">
    ${i18n.t('admin.reportReceiptImages')} (${approvedReceipts.filter(r => r.receipt_url).length})
  </h2>
  ${receiptBlocks}` : ''}

  ${refundedRegistrations.length > 0 ? `
  <!-- Refunded attendees section -->
  <h2 style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#b91c1c;margin-bottom:12px;border-top:2px solid #fecaca;padding-top:28px;">
    ${i18n.t('admin.reportRefundedSection')} (${refundedRegistrations.length})
  </h2>
  <table style="margin-bottom:16px;">
    <thead>
      <tr>
        <th>${i18n.t('admin.reportColFullName')}</th>
        <th>${i18n.t('admin.reportColArrivalDate')}</th>
        <th>${i18n.t('admin.reportColAmountPaid')}</th>
        <th>${i18n.t('admin.reportColAmountRefunded')}</th>
        <th>${i18n.t('admin.reportColPaymentMethods')}</th>
        <th>${i18n.t('admin.reportColStatus')}</th>
      </tr>
    </thead>
    <tbody>
      ${refundedTableRows}
    </tbody>
  </table>
  <div style="display:flex;gap:24px;flex-wrap:wrap;margin-bottom:32px;font-size:13px;">
    <div><span style="color:#6b7280;">${i18n.t('admin.reportRefundedIncomeReceived')}:</span> <strong style="color:#15803d;">${currency(refundedIncomeTotal)}</strong></div>
    <div><span style="color:#6b7280;">${i18n.t('admin.reportRefundedTotal')}:</span> <strong style="color:#b91c1c;">${currency(refundedTotal)}</strong></div>
  </div>
  ${refundedReceiptBlocks ? `
  <h2 style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#374151;margin-bottom:16px;">
    ${i18n.t('admin.reportReceiptImages')} (${refundedReceiptList.length})
  </h2>
  ${refundedReceiptBlocks}` : ''}` : ''}

  <!-- Footer -->
  <div style="border-top:1px solid #f3f4f6;padding-top:16px;margin-top:36px;">
    <p style="font-size:11px;color:#9ca3af;text-align:center;">
      Oasis Joven Camp &nbsp;·&nbsp; ${esc(camp.name)} &nbsp;·&nbsp; Generated ${generatedDate}
    </p>
  </div>

</body>
</html>`

  /* ── Open and print ───────────────────────────────────────────────────── */

  const win = window.open('', '_blank')
  if (!win) {
    // eslint-disable-next-line no-alert
    alert(i18n.t('admin.reportPopupBlocked'))
    return
  }

  win.document.write(html)
  win.document.close()

  // Give images ~800 ms to begin loading before the print dialog opens
  setTimeout(() => win.print(), 800)
}
